import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSidebar } from "@/domains/core/layout/useSidebar";
import { useSearch } from "@/shared/contexts/use-search";
import { cn } from "@/shared/utils";
import { ChevronLeft, Search, Bell, Settings, LogOut } from "lucide-react";
import { navigationConfig } from "./config";
import { SearchResults } from "@/components/molecules/SearchResults";

import type { NavItem } from "./types";

interface NavItemProps {
  item: NavItem;
  level?: number;
}

interface IconLinkProps {
  href: string;
  icon: React.ReactNode;
}

const IconLink: React.FC<IconLinkProps> = ({ href, icon }) => {
  return (
    <a href={href} className="text-slate-400 hover:text-white transition-colors duration-200">
      {icon}
    </a>
  );
};

// Search Bar Component
const SearchBar: React.FC = () => {
  const { searchQuery, setSearchQuery, performSearch, clearSearch, setSearchResults } = useSearch();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    // Only perform search if there's actual input
    if (value.trim()) {
      performSearch(value);
    } else {
      // Clear results when input is empty
      setSearchResults([]);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      clearSearch();
    }
  };

  return (
    <div className="relative group">
      <div className="relative">
        <input
          type="text"
          placeholder="Search here..."
          value={searchQuery}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          autoComplete="off"
          className="h-12 w-full rounded-xl border border-slate-600/50 bg-slate-800/30 backdrop-blur-sm pl-4 pr-12 text-sm text-slate-200 placeholder-slate-400 outline-none focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/20 focus:bg-slate-800/50 transition-all duration-300 shadow-lg shadow-slate-900/20 relative z-20"
        />
        <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-500/5 to-purple-500/5 opacity-0 group-focus-within:opacity-100 transition-opacity duration-300 pointer-events-none" />
        <button
          onClick={() => performSearch(searchQuery)}
          className="absolute top-1/2 right-4 -translate-y-1/2 text-slate-400 hover:text-blue-400 transition-all duration-200 hover:scale-110 z-30 pointer-events-auto"
        >
          <Search className="w-4 h-4" />
        </button>
      </div>

      {/* Search Results */}
      <SearchResults />
    </div>
  );
};

const NavItem: React.FC<NavItemProps> = ({ item, level = 0 }) => {
  const location = useLocation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const trigger = useRef<HTMLAnchorElement>(null);
  const dropdown = useRef<HTMLDivElement>(null);

  const isActive =
    location.pathname === item.href ||
    (item.children &&
      item.children.some(
        (child: NavItem) => child.href && location.pathname.startsWith(child.href),
      ));
  const hasChildren = item.children && item.children.length > 0;

  // Close on click outside
  useEffect(() => {
    const clickHandler = ({ target }: MouseEvent) => {
      if (!dropdown.current) return;
      if (
        !dropdownOpen ||
        dropdown.current.contains(target as Node) ||
        trigger.current?.contains(target as Node)
      )
        return;
      setDropdownOpen(false);
    };
    document.addEventListener("click", clickHandler);
    return () => document.removeEventListener("click", clickHandler);
  });

  // Close if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }: KeyboardEvent) => {
      if (!dropdownOpen || keyCode !== 27) return;
      setDropdownOpen(false);
    };
    document.addEventListener("keydown", keyHandler);
    return () => document.removeEventListener("keydown", keyHandler);
  });

  const content = (
    <div
      className={cn(
        "group relative flex items-center transition-all duration-300 cursor-pointer",
        "hover:bg-gradient-to-r hover:from-slate-700/30 hover:to-slate-600/20",
        "hover:shadow-lg hover:shadow-slate-900/20 hover:scale-[1.02]",
        isActive
          ? "bg-gradient-to-r from-blue-600/90 to-blue-500/80 text-white shadow-lg shadow-blue-900/30"
          : "text-slate-300 hover:text-white",
        level === 0
          ? "rounded-xl py-3 px-4 text-base font-medium mx-2"
          : "rounded-lg py-2 px-3 text-sm ml-6",
      )}
    >
      {/* Active Glow Effect */}
      {isActive && (
        <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-500/20 to-purple-500/20 blur-sm" />
      )}

      {/* Icon Container */}
      {item.icon && (
        <div className="relative flex-shrink-0 transition-all duration-300 group-hover:scale-110 group-hover:rotate-3 w-6 h-6 mr-3">
          <div
            className={cn(
              "absolute inset-0 rounded-lg transition-all duration-300",
              isActive
                ? "bg-gradient-to-br from-white/20 to-white/10"
                : "group-hover:bg-gradient-to-br group-hover:from-slate-600/30 group-hover:to-slate-500/20",
            )}
          />
          <span className="relative z-10 flex items-center justify-center w-full h-full">
            {typeof item.icon === "function" ? <item.icon size="navbar" /> : item.icon}
          </span>
        </div>
      )}

      {/* Label */}
      <span className="relative z-10 flex-1 truncate font-medium">{item.label}</span>

      {/* Badge */}
      {item.badge && (
        <span className="relative z-10 ml-2 px-2 py-1 text-xs bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-full shadow-lg">
          {item.badge}
        </span>
      )}

      {/* Dropdown Arrow */}
      {hasChildren && (
        <span
          className={cn(
            "relative z-10 ml-2 transition-all duration-300",
            "group-hover:scale-110",
            dropdownOpen ? "rotate-0" : "rotate-180",
          )}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" className="fill-current">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M4.293 5.293a1 1 0 011.414 0L8 7.586l2.293-2.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
            />
          </svg>
        </span>
      )}

      {/* Active Indicator */}
      {isActive && (
        <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-gradient-to-b from-blue-400 to-purple-400 rounded-l-full shadow-lg shadow-blue-400/50" />
      )}

      {/* Hover Glow */}
      <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-500/10 to-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
    </div>
  );

  if (item.href && !hasChildren) {
    return (
      <Link to={item.href} className="block">
        {content}
      </Link>
    );
  }

  if (hasChildren) {
    return (
      <li>
        <a
          href="#"
          ref={trigger}
          onClick={(e) => {
            e.preventDefault();
            setDropdownOpen(!dropdownOpen);
          }}
          className="block"
        >
          {content}
        </a>
        <div
          ref={dropdown}
          onFocus={() => setDropdownOpen(true)}
          onBlur={() => setDropdownOpen(false)}
          className={cn("transition-all duration-200", dropdownOpen ? "block" : "hidden")}
        >
          <ul className="px-5 py-1 space-y-1">
            {item.children?.map((child: NavItem) => (
              <li key={child.id}>
                <NavItem item={child} level={level + 1} />
              </li>
            ))}
          </ul>
        </div>
      </li>
    );
  }

  return <li>{content}</li>;
};

import type { NavSection as NavSectionType } from "./types";

const NavSection: React.FC<{
  section: NavSectionType;
}> = ({ section }) => {
  return (
    <div className="mb-8">
      {/* Section Title */}
      <div className="px-6 mb-4">
        <div className="relative">
          <h3 className="text-xs font-bold uppercase text-slate-400 tracking-widest relative z-10">
            {section.title}
          </h3>
          <div className="absolute inset-0 bg-gradient-to-r from-slate-600/20 to-transparent rounded-lg blur-sm" />
        </div>
      </div>

      {/* Section Items */}
      <nav className="px-3">
        <ul className="space-y-2">
          {section.items.map((item: NavItem) => (
            <NavItem key={item.id} item={item} />
          ))}
        </ul>
      </nav>
    </div>
  );
};

export const ExpandedSidebar: React.FC = () => {
  const { toggle } = useSidebar();

  return (
    <aside
      className="h-full w-full border-r border-slate-700/50 flex flex-col shadow-2xl shadow-slate-900/50 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900"
      style={{
        minWidth: "320px",
        maxWidth: "320px",
        height: "100vh",
      }}
    >
      <div className="flex h-full w-full flex-col">
        {/* Top Section - Fixed */}
        <div className="flex-shrink-0">
          {/* User Profile with Toggle Button */}
          <div className="pt-8 px-6 pb-8">
            <div className="flex items-center group cursor-pointer">
              <div className="relative mr-4 h-14 w-14 rounded-2xl overflow-hidden bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 flex items-center justify-center shadow-lg shadow-blue-500/25 group-hover:shadow-xl group-hover:shadow-blue-500/40 transition-all duration-300 group-hover:scale-105">
                <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent" />
                <img
                  src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face"
                  alt="Profile"
                  className="relative z-10 h-full w-full object-cover"
                />
                <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-slate-900 shadow-lg" />
              </div>
              <div className="flex-1">
                <h6 className="text-sm font-semibold text-slate-100 group-hover:text-white transition-colors duration-200">
                  Maurice
                </h6>
                <p className="text-xs text-slate-400 group-hover:text-slate-300 transition-colors duration-200">
                  Good Day 👋
                </p>
              </div>
              {/* Toggle Button */}
              <button
                onClick={toggle}
                className="group/toggle relative overflow-hidden rounded-xl transition-all duration-300 bg-gradient-to-r from-slate-800/50 to-slate-700/50 border border-slate-600/50 backdrop-blur-sm hover:from-slate-700/70 hover:to-slate-600/70 hover:border-slate-500/70 hover:shadow-lg hover:shadow-slate-900/20 active:scale-95 p-2"
                aria-label="Collapse sidebar"
              >
                {/* Animated Background */}
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20 opacity-0 group-hover/toggle:opacity-100 transition-opacity duration-300" />

                {/* Icon Container */}
                <div className="relative z-10 flex items-center justify-center">
                  <ChevronLeft className="w-4 h-4 text-slate-300 group-hover/toggle:text-white transition-colors duration-200" />
                </div>

                {/* Subtle Glow Effect */}
                <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-500/10 to-purple-500/10 opacity-0 group-hover/toggle:opacity-100 transition-opacity duration-300" />
              </button>
            </div>
          </div>

          {/* Search Bar */}
          <div className="px-6 pt-0 pb-8">
            <SearchBar />
          </div>
        </div>

        {/* Navigation Sections - No internal scrolling */}
        <div className="flex-1">
          <div className="px-6">
            {navigationConfig.sections.map((section) => (
              <NavSection key={section.id} section={section} />
            ))}
          </div>
        </div>

        {/* Bottom Section - Fixed with consistent spacing */}
        <div className="flex-shrink-0 m-6 mt-8 border-t border-slate-700/30 pt-6 pb-6">
          {/* Social Links */}
          <div className="flex h-14 w-full items-center justify-evenly rounded-2xl bg-gradient-to-r from-slate-800/40 to-slate-700/40 backdrop-blur-sm border border-slate-600/30 shadow-lg shadow-slate-900/20">
            <IconLink href="/#" icon={<Bell className="w-5 h-5" />} />
            <IconLink href="/#" icon={<Settings className="w-5 h-5" />} />
            <IconLink href="/#" icon={<LogOut className="w-5 h-5" />} />
          </div>
        </div>
      </div>
    </aside>
  );
};
